const fallbackApi = '/api/v1';
const fallbackAttachmentMaxBytes = 52_428_800;
const loopbackHostPattern = /^(localhost|127\.0\.0\.1|\[::1\]|::1)$/i;

const parsePositiveInt = (rawValue: unknown): number | null => {
  if (rawValue == null) return null;
  if (typeof rawValue !== 'string') return null;
  const trimmed = rawValue.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return null;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed)) return null;
  if (parsed <= 0) return null;
  return parsed;
};

const normalizeApiBaseUrl = (rawValue: string, browserOrigin?: string) => {
  const trimmed = rawValue.trim();
  const normalized = (() => {
    if (trimmed.startsWith('/')) {
      const base = browserOrigin ? new URL(browserOrigin).origin : 'https://blms.localhost';
      return new URL(trimmed, base).toString().replace(/\/$/, '');
    }
    return new URL(trimmed).toString().replace(/\/$/, '');
  })();

  // Accept legacy `/api` env values by promoting them to the versioned base path.
  // The API routes are registered under `/api/v1/*`.
  if (/\/api$/i.test(normalized)) {
    return `${normalized}/v1`;
  }

  return normalized;
};

const shouldPreferBrowserOriginApi = (apiBaseUrl: URL, browserOrigin: URL) =>
  apiBaseUrl.protocol === 'http:' &&
  loopbackHostPattern.test(apiBaseUrl.hostname) &&
  browserOrigin.protocol === 'https:' &&
  (browserOrigin.hostname.endsWith('.localhost') ||
    loopbackHostPattern.test(browserOrigin.hostname));

export const resolveApiBaseUrl = (rawValue: string, browserOrigin?: string) => {
  const normalized = normalizeApiBaseUrl(rawValue, browserOrigin);
  if (!browserOrigin) {
    return normalized;
  }

  const normalizedUrl = new URL(normalized);
  const originUrl = new URL(browserOrigin);
  if (!shouldPreferBrowserOriginApi(normalizedUrl, originUrl)) {
    return normalized;
  }

  // When the app is served through https://*.localhost, avoid an explicit
  // http://localhost API base that would force cross-site cookie behavior.
  const rewritten = new URL(normalizedUrl.pathname, originUrl.origin).toString();
  return rewritten.replace(/\/$/, '');
};

export const env = {
  apiBaseUrl: (() => {
    const value = import.meta.env.VITE_API_BASE_URL ?? fallbackApi;
    try {
      const browserOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
      return resolveApiBaseUrl(value, browserOrigin);
    } catch {
      return fallbackApi;
    }
  })(),
  enableServiceWorker: import.meta.env.PROD || import.meta.env.VITE_ENABLE_SW === 'true',
  appName: import.meta.env.VITE_APP_NAME ?? 'BLMS',
  gitCommit: typeof __GIT_COMMIT__ === 'string' ? __GIT_COMMIT__ : '',
  attachmentMaxBytes: (() => {
    const parsed = parsePositiveInt(import.meta.env.VITE_ATTACHMENT_MAX_BYTES);
    return parsed ?? fallbackAttachmentMaxBytes;
  })(),
};

export type AppEnv = typeof env;
